/**
 * The player's rack: tiles in the local order (minus those on the board).
 *
 * Tiles are buttons (Tab to them, Enter/Space selects, Alt+←/→ moves the
 * focused tile). Pointer taps and drags go through the drag controller, which
 * decides tap vs drag with a movement threshold; the button's own onClick only
 * handles keyboard activation (`event.detail === 0`).
 *
 * Each slot shares `layoutId="tile-<id>"` with the matching draft tile on the
 * board, so tiles glide between rack and board; reorders/shuffles animate via
 * `layout`. Newly drawn tiles fly in from the bag counter (WAAPI, 60ms stagger).
 */
import { useEffect, useMemo, useRef, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react';
import { motion } from 'motion/react';
import clsx from 'clsx';
import type { Tile } from '@wordloom/engine';
import { visibleRackIds } from '../game/draft';
import { dragController } from '../game/drag';
import { handleRackKey, prefersReducedMotion, tapRackTile } from '../game/interactions';
import { useDraftStore } from '../state/draftStore';
import { TileFace } from './Tile';
import './Rack.css';

const REORDER_SPRING = { type: 'spring', stiffness: 520, damping: 38 } as const;

interface RackProps {
  rack: readonly Tile[];
  /** Tiles can be arranged (game running and nothing in flight). */
  interactive: boolean;
}

export function tileLabel(tile: Pick<Tile, 'letter' | 'value' | 'blank'>): string {
  if (tile.blank) return 'Blank tile';
  return `${tile.letter}, ${tile.value} ${tile.value === 1 ? 'point' : 'points'}`;
}

/** Fly freshly drawn tiles in from the bag counter. */
function animateDraw(rackEl: HTMLElement, ids: readonly string[]): void {
  if (prefersReducedMotion()) return;
  const bag = Array.from(document.querySelectorAll<HTMLElement>('[data-bag-counter]'))
    .map((el) => el.getBoundingClientRect())
    .find((r) => r.width > 0 && r.height > 0);
  ids.forEach((id, i) => {
    const face = rackEl.querySelector<HTMLElement>(`[data-tile-id="${id}"] .rack__tile`);
    if (!face || typeof face.animate !== 'function') return;
    const r = face.getBoundingClientRect();
    const from = bag
      ? `translate(${bag.left + bag.width / 2 - (r.left + r.width / 2)}px, ${bag.top + bag.height / 2 - (r.top + r.height / 2)}px) scale(0.45)`
      : 'translateY(24px) scale(0.6)';
    face.animate(
      [
        { transform: from, opacity: 0 },
        { opacity: 1, offset: 0.35 },
        { transform: 'none', opacity: 1 },
      ],
      { duration: 560, delay: i * 60, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' },
    );
  });
}

export function Rack({ rack, interactive }: RackProps) {
  const rackRef = useRef<HTMLDivElement>(null);
  const rackOrder = useDraftStore((s) => s.rackOrder);
  const placements = useDraftStore((s) => s.placements);
  const selectedId = useDraftStore((s) => s.selectedId);
  const draggingId = useDraftStore((s) => s.draggingId);
  const droppedId = useDraftStore((s) => s.droppedId);
  const hoverIndex = useDraftStore((s) => s.rackHoverIndex);
  const freshIds = useDraftStore((s) => s.freshIds);

  const byId = useMemo(() => new Map(rack.map((t) => [t.id, t])), [rack]);
  const visible = useMemo(
    () => visibleRackIds(rackOrder, placements).filter((id) => byId.has(id)),
    [rackOrder, placements, byId],
  );

  useEffect(() => {
    dragController.registerRack(rackRef.current);
    return () => dragController.registerRack(null);
  }, []);

  useEffect(() => {
    if (freshIds.length === 0 || !rackRef.current) return;
    animateDraw(rackRef.current, freshIds);
    useDraftStore.getState().clearFresh();
  }, [freshIds]);

  // Insertion caret while dragging over the rack. The controller's index
  // counts slots WITHOUT the dragged tile.
  const others = draggingId ? visible.filter((id) => id !== draggingId) : visible;
  const caretBefore = hoverIndex !== null && hoverIndex < others.length ? others[hoverIndex] : null;
  const caretAfter = hoverIndex !== null && hoverIndex >= others.length ? others[others.length - 1] : null;

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>, tileId: string) => {
    if (!interactive) return;
    dragController.begin({ nativeEvent: e.nativeEvent, currentTarget: e.currentTarget }, { kind: 'rack', tileId });
  };
  const onClick = (e: MouseEvent<HTMLButtonElement>, tileId: string) => {
    // Pointer taps are handled by the drag controller; this is keyboard activation.
    if (e.detail !== 0 || !interactive) return;
    tapRackTile(tileId);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, tileId: string) => {
    if (!interactive) return;
    if (handleRackKey(e, tileId)) {
      e.preventDefault();
      e.stopPropagation();
      // Keep focus on the moved tile after the re-render.
      requestAnimationFrame(() =>
        rackRef.current?.querySelector<HTMLElement>(`[data-tile-id="${tileId}"]`)?.focus(),
      );
    }
  };

  return (
    <div
      ref={rackRef}
      className={clsx('rack', !interactive && 'rack--locked')}
      role="group"
      aria-label={`Your rack, ${visible.length} ${visible.length === 1 ? 'tile' : 'tiles'}`}
      aria-describedby="rack-help"
      data-coach="rack"
    >
      <div className="rack__tray">
        {visible.map((id) => {
          const tile = byId.get(id);
          if (!tile) return null;
          const selected = selectedId === id;
          return (
            <motion.button
              key={id}
              type="button"
              layout
              layoutId={droppedId === id ? undefined : `tile-${id}`}
              transition={{ layout: REORDER_SPRING }}
              className={clsx(
                'rack__slot',
                selected && 'is-selected',
                draggingId === id && 'is-dragging',
                caretBefore === id && 'has-caret',
                caretAfter === id && 'has-caret-after',
              )}
              data-rack-tile=""
              data-tile-id={id}
              aria-label={tileLabel(tile)}
              aria-pressed={selected}
              aria-disabled={!interactive || undefined}
              onPointerDown={(e) => onPointerDown(e, id)}
              onClick={(e) => onClick(e, id)}
              onKeyDown={(e) => onKeyDown(e, id)}
            >
              <span className="tile rack__tile" data-blank={tile.blank || undefined}>
                <TileFace letter={tile.letter} value={tile.value} blank={tile.blank} />
              </span>
            </motion.button>
          );
        })}
        {visible.length === 0 && (
          <p className="rack__empty">{rack.length === 0 ? 'Your rack is empty.' : 'All your tiles are on the board.'}</p>
        )}
      </div>
      <p id="rack-help" className="sr-only">
        Enter selects a tile. With a tile selected, Alt plus Left or Right arrow moves it within the rack.
      </p>
    </div>
  );
}
